// @flow
import { createReducer } from 'redux-create-reducer';
import * as messages from '../messages';
import * as r from 'ramda';
import { AvailabilityStatus } from '../../core/constants';
import { FQDN_MAX_LENGTH } from '../filter';

export const initialState = {
  seeded: false,
  localeLoaded: false,
  brandId: null,
  exchangeRate: null,
  query: null,
  searching: false,
  filtering: false,
  results: null,
  error: null,
  browserSession: null,
  availabilities: {},
  filters: {
    length: FQDN_MAX_LENGTH,
  },
  minTldLength: 3,
  bundle: {
    enable: false,
    includeExact: false,
  },
  bundleStatus: AvailabilityStatus.UNVERIFIED,
  features: {},
  asyncHooks: false,
  usePPE: false,
  includeTax: true,
  afternicInBasket: false,
};

export default createReducer(initialState, {
  [messages.SEEDED]: (state, action) => {
    return r.pipe(
      r.assoc('query', action.payload.query),
      r.assoc('brandId', action.payload.brandId),
      r.assoc('exchangeRate', action.payload.exchangeRate),
      r.pipe(
        r.defaultTo(3),
        r.assoc('minTldLength'),
      )(action.payload.minTldLength),
      r.assoc('unspunTlds', action.payload.unspunTlds),
      r.assoc('filters', action.payload.filters),
      r.assoc('seeded', true),
      r.assoc('bundle', action.payload.bundle),
      r.assoc('features', action.payload.features),
      r.assoc(
        'asyncHooks',
        r.defaultTo(false, action.payload.asyncHooks),
      ),
      r.assoc(
        'usePPE',
        r.defaultTo(false, action.payload.usePPE),
      ),
      r.assoc(
        'includeTax',
        r.defaultTo(false, action.payload.includeTax),
      ),
    )(state);
  },
  [messages.LOCALE_LOADED]: (state) => r.assoc('localeLoaded', true, state),
  [messages.BROWSER_SESSION_LOADED]: (state, action) => {
    return r.assoc('browserSession', action.payload.browserSession, state);
  },
  [messages.SEARCHING]: (state, action) => {
    return r.pipe(
      r.assoc('searching', true),
      r.assoc('query', action.payload.query),
      r.assoc('bundleStatus', AvailabilityStatus.UNVERIFIED),
    )(state);
  },
  [messages.SEARCH_RESULTS_RECEIVED]: (state, action) => {
    return r.pipe(
      r.assoc('searching', false),
      r.assoc('filtering', false),
      r.assoc('results', action.payload.results),
      r.assoc('error', null),
      r.assoc('afternicInBasket', false),
    )(state);
  },
  [messages.SEARCH_FAILURE]: (state, action) => {
    return r.pipe(
      r.assoc('searching', false),
      r.assoc('filtering', false),
      r.assoc('error', action.payload),
      r.assoc('results', null),
      r.assoc('afternicInBasket', false),
    )(state);
  },
  [messages.VERIFYING]: (state, action) => {
    return r.pipe(
      r.assocPath([
        'availabilities',
        action.payload.fqdn,
      ],
      AvailabilityStatus.VERIFYING),
    )(state);
  },
  [messages.VERIFYING_COMPLETE]: (state, action) => {
    return r.pipe(
      r.assocPath([
        'availabilities',
        action.payload.fqdn,
      ],
      action.payload.status),
    )(state);
  },
  [messages.VERIFYING_FAILED]: (state, action) => r.assocPath(
    [ 'availabilities', action.payload.fqdn ],
    AvailabilityStatus.UNVERIFIED,
    state,
  ),
  [messages.PURCHASING_BUNDLE]: (state, action) => r.pipe(
    // set the availability of the individual bundle fqdns
    // so if they appear in the search results, they will be shown as verifying
    r.over(
      r.lensProp('availabilities'),
      r.reduce(
        r.flip(r.assoc(r.__, AvailabilityStatus.VERIFYING)),
        r.__,
        action.payload.bundles,
      ),
    ),
    r.assoc(
      'bundleStatus',
      AvailabilityStatus.VERIFYING,
    ),
  )(state),
  [messages.BUNDLE_PURCHASED]: (state, action) => r.pipe(
    // show all of the bundle fqdns in the search results as added to basket
    r.over(
      r.lensProp('availabilities'),
      r.reduce(
        r.flip(
          r.assoc(r.__, AvailabilityStatus.AVAILABLE),
        ),
        r.__,
        action.payload.bundles,
      ),
    ),
    r.assoc(
      'bundleStatus',
      AvailabilityStatus.AVAILABLE,
    ),
  )(state),
  [messages.BUNDLE_FAILED]: (state, action) => r.pipe(
    r.over(
      r.lensProp('availabilities'),
      r.reduce(
        r.flip(r.assoc(r.__, AvailabilityStatus.UNVERIFIED)),
        r.__,
        action.payload.bundles,
      ),
    ),
    r.assoc(
      'bundleStatus',
      AvailabilityStatus.UNVERIFIED,
    ),
  )(state),
  [messages.FILTERING]: (state, action) => {
    return r.pipe(
      r.assoc('filtering', true),
      r.assoc('filters', action.payload.filters),
    )(state);
  },
  [messages.FILTERED]: (state, action) => {
    return r.pipe(
      r.assoc('filters', action.payload.filters),
    )(state);
  },
  [messages.DOMAIN_NEGOTIATED]: r.assoc('afternicInBasket', true),
});
